import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

interface ModalProps {
    open: boolean;
    onClose?: () => void;
    title?: string;
    children: ReactNode;
    className?: string;
    width?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
}

const widthClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-[95vw]',
};

export function Modal({ open, onClose, title, children, className, width = 'md' }: ModalProps) {
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center animate-fadeIn">
            <div
                className="absolute inset-0 bg-[#24373b]/35 backdrop-blur-[1px] transition-opacity duration-200"
                onClick={onClose}
            />
            <div
                className={cn(
                    'relative z-10 w-full rounded-lg border border-[#8fd3d0] bg-white shadow-2xl transition-transform duration-200',
                    widthClasses[width],
                    'max-h-[90vh] overflow-hidden flex flex-col',
                    className,
                )}
            >
                {title && (
                    <div className="flex items-center justify-between border-b border-[#e5eded] px-5 py-4">
                        <h2 className="text-base font-semibold text-zinc-900">{title}</h2>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                )}
                <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
                    {children}
                </div>
            </div>
        </div>
    );
}

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string;
    cancelText?: string;
}

export function ConfirmDialog({
    open,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText = '确认',
    cancelText = '取消',
}: ConfirmDialogProps) {
    return (
        <Modal open={open} onClose={onCancel} title={title} width="sm">
            <p className="text-sm text-zinc-600">{message}</p>
            <div className="mt-6 flex justify-end gap-2">
                <button
                    onClick={onCancel}
                    className="h-9 rounded-md border border-zinc-300 px-4 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                >
                    {cancelText}
                </button>
                <button
                    onClick={onConfirm}
                    className="h-9 rounded-md bg-red-500 px-4 text-sm font-medium text-white hover:bg-red-600"
                >
                    {confirmText}
                </button>
            </div>
        </Modal>
    );
}
